/**
 * Iter 243 · Public Client RFQ Portal (v2)
 *
 * Workspace-branded, responsive, no-login page reached via magic link
 * /client/rfq/:token. Client can:
 *   - Review the RFQ lines
 *   - Propose new lines / additional qty (lands in client-proposed bucket)
 *   - Leave comments
 *   - **Smart approve** — if any changes have been made in this session
 *     (line added, comment posted), the primary button becomes
 *     "Request Revision"; if nothing changed, they can just approve
 *     with an optional signature note.
 */
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Send, FileText, CheckCircle2, XCircle, MessageSquare,
         Lock, Sparkles, Package, Paperclip, Upload, Download, Trash2 } from "lucide-react";
import PortalRichText from "@/components/public/PortalRichText";
import { apiErrorMessage } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function ClientRfqPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [qty, setQty] = useState(1);
  const [unit, setUnit] = useState("unit");
  const [note, setNote] = useState("");
  const [catalogPick, setCatalogPick] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [decisionNote, setDecisionNote] = useState("");
  const [commentDraft, setCommentDraft] = useState("");
  const [sessionChanged, setSessionChanged] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/public/rfq/${token}`);
      setData(res.data);
    } catch (e) {
      setData({ error: e?.response?.data?.detail || "Link invalid or expired" });
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  // Iter 245 · Pre-populate signer + contact from the RFQ's client_requestor block
  useEffect(() => {
    if (!data?.rfq) return;
    if (!signerName && data.rfq.client_requestor_name) setSignerName(data.rfq.client_requestor_name);
    if (!signerEmail && data.rfq.client_requestor_email) setSignerEmail(data.rfq.client_requestor_email);
     
  }, [data?.rfq?.id]);

  const branding = data?.branding || {};
  const rfq = data?.rfq || null;
  const catalog = data?.catalog || [];
  const proposed = data?.proposed_lines || [];
  const approvals = data?.approval_history || [];
  const attachments = data?.attachments || [];
  const isLocked = rfq && (rfq.status === "approved" || rfq.status === "rejected");

  // Any client action this session → block plain Approve, force Request Revision
  const canJustApprove = !sessionChanged && proposed.length === 0;

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const uploadFile = async (f) => {
    if (!f) return;
    if (f.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25 MB limit"); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await axios.post(`${API}/public/rfq/${token}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${f.name}`);
      setSessionChanged(true);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Upload failed"));
    } finally { setUploading(false); }
  };

  const removeAttachment = async (aid) => {
    try {
      await axios.delete(`${API}/public/rfq/${token}/attachments/${aid}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const addLine = async () => {
    if (!name.trim() && !catalogPick) {
      toast.error("Enter a name or pick from catalog"); return;
    }
    try {
      const item = catalog.find((c) => c.id === catalogPick);
      const payload = {
        catalog_item_id: catalogPick || undefined,
        name: item?.name || name.trim(),
        qty: Number(qty) || 1,
        unit: item?.unit || unit,
        note: note.trim() || undefined,
        proposer_name: signerName || undefined,
        proposer_email: signerEmail || undefined,
      };
      await axios.post(`${API}/public/rfq/${token}/lines`, payload);
      toast.success("Item added — team will review your suggestion");
      setName(""); setQty(1); setNote(""); setCatalogPick("");
      setSessionChanged(true);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const removeProposed = async (pid) => {
    try {
      await axios.delete(`${API}/public/rfq/${token}/proposed-lines/${pid}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const postComment = async () => {
    if (!signerName.trim()) { toast.error("Please enter your name first"); return; }
    // Strip HTML tags to check for actual content
    const plainText = commentDraft.replace(/<[^>]+>/g, "").trim();
    if (!plainText) return;
    try {
      await axios.post(`${API}/public/rfq/${token}/comment`, {
        author_name: signerName, author_email: signerEmail || undefined,
        body: commentDraft, body_format: "html",
      });
      toast.success("Comment posted");
      setCommentDraft("");
      setSessionChanged(true);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const decide = async (decision) => {
    if (!signerName.trim()) { toast.error("Please enter your name to sign"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/rfq/${token}/approve`, {
        decision, signer_name: signerName,
        signer_email: signerEmail || undefined,
        note: decisionNote.trim() || undefined,
      });
      toast.success(decision === "approved" ? "Approved — thank you!"
        : decision === "request_revision" ? "Revision requested"
        : "Rejected");
      setDecisionNote("");
      setSessionChanged(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };

  const cssVars = useMemo(() => ({
    "--brand": branding.primary_color || "#0f172a",
    "--brand-accent": branding.accent_color || "#0284c7",
  }), [branding]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;
  if (data?.error) return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md text-center border border-slate-200 bg-white p-8 rounded-md shadow-sm">
        <XCircle className="w-10 h-10 mx-auto text-rose-500" />
        <h1 className="text-lg font-bold mt-3">Link unavailable</h1>
        <p className="text-sm text-slate-500 mt-1">{data.error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" style={cssVars} data-testid="client-rfq-portal">
      {/* Branded header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.name}
                    className="h-10 w-auto shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded shrink-0 flex items-center justify-center text-white font-bold"
                    style={{ background: "var(--brand)" }}>
                {(branding.name || "W")[0]}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Presented by</div>
              <div className="font-semibold truncate">{branding.name}</div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">RFQ</div>
            <div className="font-mono text-sm">{rfq?.number}</div>
            {rfq?.revision_no > 1 && (
              <div className="text-[10px] text-slate-500">Rev {rfq.revision_no}</div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Title + summary card */}
        <section className="bg-white border border-slate-200 p-5 rounded-md">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {rfq?.priority && rfq.priority !== "normal" && (
                  <span className={`text-[10px] px-1.5 py-0.5 border uppercase tracking-widest font-semibold ${
                    rfq.priority === "urgent" ? "bg-rose-50 text-rose-700 border-rose-300"
                    : rfq.priority === "high" ? "bg-amber-50 text-amber-700 border-amber-300"
                    : "bg-slate-50 text-slate-600 border-slate-300"}`}
                    data-testid="rfq-portal-priority">
                    {rfq.priority} priority
                  </span>
                )}
                {rfq?.status && (
                  <span className="text-[10px] px-1.5 py-0.5 border border-slate-300 bg-slate-50 text-slate-700 uppercase tracking-widest">
                    {rfq.status.replace(/_/g, " ")}
                  </span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: "var(--brand)" }}>
                {rfq?.title}
              </h1>
              {rfq?.description && (
                <p className="text-sm text-slate-600 mt-1.5">{rfq.description}</p>
              )}
              {rfq?.requested_delivery && (
                <div className="text-xs text-slate-500 mt-2">
                  Requested delivery: <b>{rfq.requested_delivery}</b>
                </div>
              )}
            </div>
            <div className="shrink-0">
              {isLocked ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-700 text-[11px] uppercase tracking-widest font-semibold">
                  <Lock className="w-3 h-3" /> {rfq.status === "approved" ? "Approved" : "Rejected"}
                </span>
              ) : (
                <span className="inline-block px-2 py-1 border border-sky-300 bg-sky-50 text-sky-700 text-[11px] uppercase tracking-widest font-semibold">
                  Awaiting your review
                </span>
              )}
            </div>
          </div>

          {/* Client + Internal POC contact strip */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-200 text-xs">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">For (Client)</div>
              <div className="mt-0.5 font-semibold">{rfq?.client_name || rfq?.client_requestor_name || "—"}</div>
              {rfq?.client_requestor_name && rfq.client_requestor_name !== rfq.client_name && (
                <div className="text-slate-600">{rfq.client_requestor_name}</div>
              )}
              {rfq?.client_requestor_email && <div className="text-slate-500">{rfq.client_requestor_email}</div>}
              {rfq?.client_requestor_phone && <div className="text-slate-500">{rfq.client_requestor_phone}</div>}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">{branding.name || "Workspace"} contact for this RFQ</div>
              <div className="mt-0.5 font-semibold">{rfq?.owner_name || rfq?.requested_by_name || "—"}</div>
              {rfq?.requested_by_email && <div className="text-slate-500"><a href={`mailto:${rfq.requested_by_email}`} className="hover:underline">{rfq.requested_by_email}</a></div>}
              {rfq?.requested_by_phone && <div className="text-slate-500"><a href={`tel:${rfq.requested_by_phone}`} className="hover:underline">{rfq.requested_by_phone}</a></div>}
            </div>
          </div>
        </section>

        {/* Line items */}
        <section className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="text-sm font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4" /> Requested items ({(rfq?.line_items || []).length})
            </div>
          </div>
          {(rfq?.line_items || []).length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">No items listed yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase text-slate-500 tracking-wide">
                <tr>
                  <th className="px-4 py-2 text-left w-10">#</th>
                  <th className="px-4 py-2 text-left">Item</th>
                  <th className="px-4 py-2 text-right w-20">Qty</th>
                  <th className="px-4 py-2 text-left w-20">Unit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {(rfq.line_items || []).map((l, i) => (
                  <tr key={l.id}>
                    <td className="px-4 py-3 text-slate-400 tabular-nums">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{l.name}</div>
                      {l.description && <div className="text-xs text-slate-500 mt-0.5">{l.description}</div>}
                      {l.note && <div className="text-xs text-slate-500 italic mt-0.5">&ldquo;{l.note}&rdquo;</div>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-mono">{l.qty}</td>
                    <td className="px-4 py-3 text-slate-500">{l.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {/* Awaiting team review (client-proposed lines) — Iter 245: placed
            right below Requested items so context flows naturally */}
        {proposed.length > 0 && (
          <section className="border border-amber-300 bg-amber-50/40 rounded-md" data-testid="rfq-portal-proposed">
            <div className="px-4 py-2 border-b border-amber-300 text-xs font-semibold uppercase tracking-widest text-amber-800 flex items-center gap-2">
              <Package className="w-3.5 h-3.5" /> Awaiting team review ({proposed.length})
            </div>
            <div className="divide-y divide-amber-200">
              {proposed.map((p) => (
                <div key={p.id} className="p-3 flex items-start gap-3 text-sm" data-testid={`rfq-portal-proposed-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-amber-800/80">
                      {p.qty} {p.unit || "unit"}
                      {p.note && ` · ${p.note}`}
                    </div>
                  </div>
                  <button type="button" onClick={() => removeProposed(p.id)}
                           className="text-[11px] text-slate-500 hover:text-rose-600"
                           data-testid={`rfq-portal-proposed-remove-${p.id}`}>
                    Withdraw
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Add item */}
        {!isLocked && (
          <section className="bg-white border border-slate-200 rounded-md" data-testid="rfq-portal-add-item">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <Sparkles className="w-4 h-4" style={{ color: "var(--brand-accent)" }} />
              <div className="text-sm font-semibold">Missing something? Add it below</div>
            </div>
            <div className="p-4 space-y-3">
              {catalog.length > 0 && (
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-slate-500">Or pick from catalog</label>
                  <select value={catalogPick}
                           onChange={(e) => setCatalogPick(e.target.value)}
                           className="w-full text-sm px-2 py-2 mt-1 border border-slate-300 rounded bg-white"
                           data-testid="rfq-portal-catalog-select">
                    <option value="">— Free-type below —</option>
                    {catalog.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.category ? ` · ${c.category}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                <div className="sm:col-span-3">
                  <label className="text-[10px] uppercase tracking-wide text-slate-500">Item name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)}
                          placeholder="e.g. Additional social poster"
                          className="w-full text-sm px-2 py-2 mt-1 border border-slate-300 rounded"
                          data-testid="rfq-portal-name" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-slate-500">Qty</label>
                  <input type="number" value={qty} onChange={(e) => setQty(e.target.value)}
                          className="w-full text-sm px-2 py-2 mt-1 border border-slate-300 rounded tabular-nums"
                          data-testid="rfq-portal-qty" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-slate-500">Unit</label>
                  <input value={unit} onChange={(e) => setUnit(e.target.value)}
                          className="w-full text-sm px-2 py-2 mt-1 border border-slate-300 rounded"
                          data-testid="rfq-portal-unit" />
                </div>
                <div className="sm:col-span-6">
                  <label className="text-[10px] uppercase tracking-wide text-slate-500">Note (optional)</label>
                  <input value={note} onChange={(e) => setNote(e.target.value)}
                          placeholder="Colours, references, deadline nuances…"
                          className="w-full text-sm px-2 py-2 mt-1 border border-slate-300 rounded"
                          data-testid="rfq-portal-note" />
                </div>
              </div>
              <div className="flex justify-end">
                <button type="button" onClick={addLine}
                         className="text-sm px-4 py-2 text-white font-medium flex items-center gap-1.5 hover:opacity-90"
                         style={{ background: "var(--brand-accent)" }}
                         data-testid="rfq-portal-add-btn">
                  <Plus className="w-3.5 h-3.5" /> Add to my proposal
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Attachments — Iter 246 · client can attach reference files */}
        <section className="bg-white border border-slate-200 rounded-md" data-testid="rfq-portal-attachments">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <Paperclip className="w-4 h-4" /> Attachments ({attachments.length})
          </div>
          <div className="p-4 space-y-3">
            {attachments.length === 0 && (
              <div className="text-xs text-slate-400 italic">No files attached yet.</div>
            )}
            {attachments.length > 0 && (
              <div className="space-y-1.5">
                {attachments.map((a) => {
                  const isMine = a.source === "client_portal" || a.uploader_kind === "client_portal";
                  const kb = Math.round((a.size || 0) / 1024);
                  return (
                    <div key={a.id} className="flex items-center gap-2 text-sm border border-slate-200 px-3 py-2 rounded"
                          data-testid={`rfq-portal-attach-${a.id}`}>
                      <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{a.filename}</div>
                        <div className="text-[10px] text-slate-500">
                          {kb.toLocaleString()} KB
                          {isMine && <span className="ml-2 text-emerald-700">· uploaded by you</span>}
                          {!isMine && a.uploader_name && <span className="ml-2">· from {a.uploader_name}</span>}
                        </div>
                      </div>
                      <a href={`${API}/public/rfq/${token}/attachments/${a.id}/download`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-[11px] text-slate-600 hover:text-slate-900 flex items-center gap-1"
                          data-testid={`rfq-portal-attach-download-${a.id}`}>
                        <Download className="w-3.5 h-3.5" /> Open
                      </a>
                      {isMine && !isLocked && (
                        <button type="button" onClick={() => removeAttachment(a.id)}
                                 className="text-[11px] text-slate-400 hover:text-rose-600 flex items-center gap-1"
                                 data-testid={`rfq-portal-attach-remove-${a.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {!isLocked && (
              <div>
                <input ref={fileInputRef} type="file" className="hidden"
                        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ""; }}
                        data-testid="rfq-portal-attach-input" />
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                         className="text-sm px-3 py-1.5 border border-slate-300 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50"
                         data-testid="rfq-portal-attach-btn">
                  <Upload className="w-3.5 h-3.5" />
                  {uploading ? "Uploading…" : "Attach a file"}
                </button>
                <div className="text-[10px] text-slate-400 mt-1">
                  Images, PDF, Word, Excel, PowerPoint, or plain text · max 25 MB per file
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Comments */}
        <section className="bg-white border border-slate-200 rounded-md" data-testid="rfq-portal-comments">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Comments &amp; questions
          </div>
          <div className="p-4 space-y-3">
            {!isLocked && (
              <>
                <PortalRichText
                  value={commentDraft}
                  onChange={setCommentDraft}
                  placeholder="Ask a question, share references, or leave feedback…"
                  testid="rfq-portal-comment-input"
                />
                <div className="flex justify-end">
                  <button type="button" onClick={postComment} disabled={!commentDraft.replace(/<[^>]+>/g, "").trim()}
                           className="text-sm px-4 py-2 border border-slate-300 hover:bg-slate-50 font-medium flex items-center gap-1.5 disabled:opacity-50"
                           data-testid="rfq-portal-comment-post">
                    <Send className="w-3.5 h-3.5" /> Post comment
                  </button>
                </div>
              </>
            )}
            <div className="space-y-2">
              {(data?.comments || []).slice(0, 8).map((c) => (
                <div key={c.id} className="text-sm border-l-2 border-slate-300 pl-3 py-1">
                  <div className="text-[10px] text-slate-500 uppercase tracking-widest">
                    <b className="text-slate-700 normal-case">{c.author_name || "Team"}</b>
                    {c.source === "client_portal" && <span> · Client</span>}
                    <span> · {new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  {c.body_format === "html" ? (
                    <div className="prose prose-sm max-w-none mt-0.5"
                          // Body was sanitised server-side (see routes_contracts._sanitize_portal_html).
                          dangerouslySetInnerHTML={{ __html: c.body }} />
                  ) : (
                    <div className="mt-0.5 whitespace-pre-wrap">{c.body}</div>
                  )}
                </div>
              ))}
              {(data?.comments || []).length === 0 && (
                <div className="text-xs text-slate-400 italic">No comments yet.</div>
              )}
            </div>
          </div>
        </section>

        {/* Approval history */}
        {approvals.length > 0 && (
          <section className="bg-white border border-slate-200 rounded-md" data-testid="rfq-portal-history">
            <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold">Approval history</div>
            <div className="p-4 space-y-2 text-xs">
              {approvals.slice(0, 10).map((a) => (
                <div key={a.id} className="flex items-start gap-2">
                  <span className={`shrink-0 mt-0.5 text-[9px] px-1.5 py-0.5 border uppercase tracking-widest ${
                    a.decision === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                    : a.decision === "rejected" ? "bg-rose-50 text-rose-700 border-rose-300"
                    : a.decision === "request_revision" ? "bg-amber-50 text-amber-700 border-amber-300"
                    : "bg-slate-50 text-slate-700 border-slate-300"}`}>
                    {(a.decision || "").replace(/_/g, " ")}
                  </span>
                  <div>
                    <b>{a.actor_name}</b>
                    <span className="text-slate-500"> · rev {a.revision_no || 1} · {new Date(a.at).toLocaleDateString()}</span>
                    {a.note && <div className="italic text-slate-500">&ldquo;{a.note}&rdquo;</div>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Sign & decide */}
        {!isLocked && (
          <section className="bg-white border-2 rounded-md" style={{ borderColor: "var(--brand)" }} data-testid="rfq-portal-decide">
            <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold" style={{ color: "var(--brand)" }}>
              Your decision
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-slate-500">Your name</label>
                  <input value={signerName} onChange={(e) => setSignerName(e.target.value)}
                          placeholder="Signing as…"
                          className="w-full text-sm px-2 py-2 mt-1 border border-slate-300 rounded"
                          data-testid="rfq-portal-signer-name" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-slate-500">Email (optional)</label>
                  <input value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="w-full text-sm px-2 py-2 mt-1 border border-slate-300 rounded"
                          data-testid="rfq-portal-signer-email" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Optional note</label>
                <textarea value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)}
                           rows={2} placeholder="Anything the team should know…"
                           className="w-full text-sm px-2 py-2 mt-1 border border-slate-300 rounded resize-none"
                           data-testid="rfq-portal-decision-note" />
              </div>
              {!canJustApprove && (
                <div className="text-[11px] px-3 py-2 bg-amber-50 text-amber-800 border border-amber-200">
                  You&apos;ve added items or comments this session — the team needs to
                  update the RFQ before it can be approved. Send it back for
                  revision below.
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <button type="button" onClick={() => decide("rejected")}
                         disabled={busy || !signerName.trim()}
                         className="text-sm px-4 py-2 border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50 font-medium flex items-center justify-center gap-1.5"
                         data-testid="rfq-portal-reject">
                  <XCircle className="w-3.5 h-3.5" /> Reject
                </button>
                <button type="button" onClick={() => decide("request_revision")}
                         disabled={busy || !signerName.trim()}
                         className="text-sm px-4 py-2 border border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50 font-medium flex items-center justify-center gap-1.5"
                         data-testid="rfq-portal-request-revision">
                  Request revision
                </button>
                <button type="button" onClick={() => decide("approved")}
                         disabled={busy || !signerName.trim() || !canJustApprove}
                         className="text-sm px-5 py-2 text-white hover:opacity-90 disabled:opacity-50 font-semibold flex items-center justify-center gap-1.5"
                         style={{ background: canJustApprove ? "var(--brand)" : "#94a3b8" }}
                         title={canJustApprove ? "Approve as-is" : "Cannot approve — you've made changes this session"}
                         data-testid="rfq-portal-approve">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                </button>
              </div>
            </div>
          </section>
        )}
      </main>

      <footer className="max-w-4xl mx-auto px-4 sm:px-6 py-6 text-center text-[11px] text-slate-400">
        {branding.footer || `Presented securely by ${branding.name}. Any changes are logged with your name and time.`}
      </footer>
    </div>
  );
}
